import { FieldNumberFormat } from 'components/FieldNumberFormat';
import type { TextFieldProps, Direction } from 'uikit';
import { Tooltip } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { Control, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

export function ControlNumberField({
    name,
    control,
    rules,
    clearOnDisabled,
    direction = 'rightCenter',
    isTooltipHidden,
    hideOnDisabled,
    classNameTooltip,
    customStaticTooltip,
    onTooltipAttributeChange,
    placeholder,
    onChangeField,
    decimalScale,
    fixedDecimalScale = true,
    ...props
}: TextFieldProps<'text' | 'number' | 'textarea'> &
    Omit<UseControllerProps, 'control'> & {
        clearOnDisabled?: boolean;
        decimalScale?: number;
        direction?: Direction;
        isTooltipHidden?: boolean;
        hideOnDisabled?: boolean;
        classNameTooltip?: string;
        customStaticTooltip?: boolean;
        control?: Control<any, any>;
        onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
        placeholder?: string;
        onChangeField?: (value: string) => void;
        fixedDecimalScale?: boolean;
    } & { texRight?: boolean; thousandSeparator?: boolean | string; inputStyle?: CSSProperties }) {
    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const handleOnChange = useCallback(
        ({ value }) => {
            field.onChange(value);
            onChangeField?.(value);
        },
        [field, onChangeField],
    );

    const inputRef = useRef(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (field.value && props.disabled && clearOnDisabled) {
            field.onChange(null);
        }
    }, [clearOnDisabled, field, props.disabled]);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    const currentValue = props.disabled && hideOnDisabled ? '' : String(field.value ?? '').replace('.', ',');
    const isCustomTooltip = !isTooltipHidden && fieldState.invalid && fieldState.error?.message && customStaticTooltip;

    return (
        <>
            <FieldNumberFormat
                value={currentValue}
                status={fieldState.invalid ? 'alert' : undefined}
                onChange={handleOnChange}
                inputContainerRef={inputRef}
                isCustomTooltip={!!isCustomTooltip}
                errorMessage={fieldState.error?.message}
                placeholder={placeholder || 'Введите'}
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
                {...props}
            />
            {!isTooltipHidden && fieldState.invalid && fieldState.error?.message && !customStaticTooltip && (
                <Tooltip
                    size="s"
                    direction={direction}
                    possibleDirections={[direction]}
                    status="alert"
                    anchorRef={inputRef}
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {fieldState.error?.message}
                </Tooltip>
            )}
        </>
    );
}

ControlNumberField.displayName = 'ControlNumberField';
